import type { Subscription } from "@rails/actioncable"
import { useEffect, useRef } from "react"

import { getConsumer } from "./consumer"

interface ChannelOptions<TActions extends string = string> {
  props?: object
  actions?: TActions[]
}

interface CableHookReturn {
  leave: () => void
  send: (data: object) => void
}

export const useCable = <TEvent = object, TActions extends string = string>(
  channelName: string,
  options: ChannelOptions<TActions>,
  callback: (event: TEvent) => void,
): CableHookReturn => {
  const subscriptionRef = useRef<Subscription | null>(null)

  const consumer = getConsumer()

  useEffect(() => {
    if (!consumer) return

    subscriptionRef.current = consumer.subscriptions.create(
      {
        channel: channelName,
        ...options.props,
      },
      {
        received: (data: TEvent) => {
          callback(data)
        },
      },
    )
    return () => {
      subscriptionRef.current?.unsubscribe()
      subscriptionRef.current = null
    }
  }, [channelName, options.props, callback])

  const leave = (): void => {
    subscriptionRef.current?.unsubscribe()
    subscriptionRef.current = null
  }

  const send = (data: object): void => {
    subscriptionRef.current?.send(data)
  }

  return { leave, send }
}
